import { useEffect, useRef, useState } from 'react';
import { Button, Spinner, useToast } from '@shared/ui';
import { mediaApi, type MediaLink, type MediaScope } from '@entities/media';
import { cn } from '@shared/lib/cn';

const MAX_SIZE_BYTES = 50 * 1024 * 1024;

/** What we learned locally about a file uploaded in this session. */
interface LocalMeta {
  name: string;
  contentType: string;
  previewUrl?: string;
}

interface PendingUpload {
  key: number;
  name: string;
  error?: string;
}

let pendingKeySeq = 0;

export function DocumentUploader({
  scope,
  label,
  multiple = true,
  mediaIds,
  onChange,
  accept = 'application/pdf,image/*',
  existing = [],
}: {
  scope: MediaScope;
  label: string;
  multiple?: boolean;
  mediaIds: string[];
  onChange: (ids: string[]) => void;
  accept?: string;
  /** Already-persisted links, so previously saved files render with a real preview. */
  existing?: MediaLink[];
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [meta, setMeta] = useState<Record<string, LocalMeta>>({});
  const objectUrls = useRef<string[]>([]);
  const toast = useToast();

  useEffect(() => {
    const urls = objectUrls.current;
    return () => urls.forEach(URL.revokeObjectURL);
  }, []);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    // `mediaIds` is a prop and stays stale inside this loop — accumulate locally
    // so uploading several files in a row doesn't drop the earlier ones.
    let current = multiple ? [...mediaIds] : [];

    for (const file of list) {
      if (file.size > MAX_SIZE_BYTES) {
        toast.error(`${file.name} слишком большой`, 'Максимум 50 MB');
        continue;
      }

      const key = ++pendingKeySeq;
      setPending((s) => [...s, { key, name: file.name }]);

      try {
        const id = await mediaApi.uploadFile(scope, file);

        const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
        if (previewUrl) objectUrls.current.push(previewUrl);
        setMeta((m) => ({ ...m, [id]: { name: file.name, contentType: file.type, previewUrl } }));

        current = multiple ? [...current, id] : [id];
        onChange(current);
        // Drop from pending — otherwise the file shows twice: once as a pending
        // row and once as a persisted media id.
        setPending((s) => s.filter((p) => p.key !== key));
      } catch (e) {
        setPending((s) =>
          s.map((p) => (p.key === key ? { ...p, error: (e as Error).message } : p)),
        );
        toast.error(`Не удалось загрузить ${file.name}`, (e as Error).message);
      }
    }

    if (inputRef.current) inputRef.current.value = '';
  };

  const remove = (id: string) => onChange(mediaIds.filter((x) => x !== id));

  const items = mediaIds.map((id) => {
    const local = meta[id];
    const saved = existing.find((e) => e.id === id);
    return {
      id,
      name: local?.name ?? null,
      url: local?.previewUrl ?? saved?.url ?? null,
      contentType: local?.contentType ?? saved?.contentType ?? null,
    };
  });

  const occupied = !multiple && mediaIds.length > 0;
  const isEmpty = items.length === 0 && pending.length === 0;

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium">{label}</div>
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()}>
          {occupied ? 'Заменить' : '+ Загрузить'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          hidden
          onChange={(e) => void handleFiles(e.target.files)}
        />
      </div>

      {isEmpty ? (
        <p className="text-xs text-muted-foreground">Файлы не прикреплены · до 50 MB</p>
      ) : (
        <ul className="space-y-1">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-2 rounded border bg-background px-2 py-1.5"
            >
              <Thumb url={item.url} contentType={item.contentType} />
              <span className="min-w-0 flex-1 truncate text-xs">
                {item.name ?? <span className="font-mono text-muted-foreground">{item.id.slice(0, 8)}…</span>}
              </span>
              <button
                type="button"
                onClick={() => remove(item.id)}
                title="Открепить"
                aria-label="Открепить"
                className="shrink-0 rounded px-1 text-muted-foreground hover:text-destructive"
              >
                ✕
              </button>
            </li>
          ))}

          {pending.map((p) => (
            <li
              key={p.key}
              className={cn(
                'flex items-center gap-2 rounded border px-2 py-1.5',
                p.error ? 'border-destructive/40 bg-destructive/5' : 'bg-muted/40',
              )}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center">
                {p.error ? <span className="text-xs text-destructive">!</span> : <Spinner className="h-3 w-3" />}
              </span>
              <span className="min-w-0 flex-1 truncate text-xs">
                {p.name}
                {p.error ? (
                  <span className="block truncate text-[10px] text-destructive">{p.error}</span>
                ) : null}
              </span>
              {p.error ? (
                <button
                  type="button"
                  onClick={() => setPending((s) => s.filter((x) => x.key !== p.key))}
                  title="Убрать"
                  aria-label="Убрать"
                  className="shrink-0 rounded px-1 text-muted-foreground hover:text-destructive"
                >
                  ✕
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Thumb({ url, contentType }: { url: string | null; contentType: string | null }) {
  if (url && contentType?.startsWith('image/')) {
    return <img src={url} alt="" className="h-8 w-8 shrink-0 rounded border object-cover" />;
  }
  const ext = contentType?.split('/')[1]?.slice(0, 4) ?? 'file';
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded border bg-muted font-mono text-[9px] uppercase text-muted-foreground">
      {ext}
    </span>
  );
}
